import React, { useState, useEffect } from "react";
import { StyleSheet, ScrollView, View, Pressable, Platform } from "react-native";
import { router, usePathname } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { CheckboxRow } from "@/components/ui/CheckboxRow";
import { useTheme } from "@/hooks/useTheme";
import { MaterialIcons } from "@expo/vector-icons";
import { useApply } from "@/contexts/ApplyContext";
import { applicationService } from "@/services/applicationService";

const STEP_INDEX = 4;

export default function ReviewScreen() {
  const { colors } = useTheme();
  const { formData, resetFormData, updateStep, clearSavedData } = useApply();
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const pathname = usePathname();

  // Track current step (only when this screen is active)
  useEffect(() => {
    if (pathname.includes("/review")) {
      updateStep(STEP_INDEX);
    }
  }, [pathname, updateStep]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    const source = Platform.OS === "web" ? "web" : "mobile";

    const result = await applicationService.submitApplication(
      {
        authorizedToWork: formData.authorizedToWork ?? false,
        over18: formData.over18 ?? false,
        previouslyWorked: formData.previouslyWorked,
        firstName: formData.firstName,
        middleInitial: formData.middleInitial || undefined,
        lastName: formData.lastName,
        preferredName: formData.preferredName || undefined,
        email: formData.email,
        phone: formData.phone,
        streetLine1: formData.streetLine1,
        streetLine2: formData.streetLine2 || undefined,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        office: formData.office || undefined,
        referralSource: formData.referralSource,
        referralName: formData.referralName || undefined,
        physicalRequirements: formData.physicalRequirements,
        heatInjury: formData.heatInjury,
        hasTransportation: formData.hasTransportation,
        eventServicesExperience: formData.eventServicesExperience,
        hasCellPhone: formData.hasCellPhone,
        hasAdditionalInfo: formData.hasAdditionalInfo,
        additionalInfo: formData.additionalInfo || undefined,
      },
      source,
    );

    setSubmitting(false);

    if (result.success) {
      clearSavedData();
      setSubmitted(true);
    } else {
      setError(result.error || "Failed to submit application. Please try again.");
    }
  };

  const handleStartOver = () => {
    resetFormData();
    router.replace("/apply");
  };

  const SummaryCard = ({
    title,
    icon,
    route,
    children,
  }: {
    title: string;
    icon: keyof typeof MaterialIcons.glyphMap;
    route: string;
    children: React.ReactNode;
  }) => (
    <View style={[styles.summaryCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
      <View style={styles.summaryHeader}>
        <View style={styles.summaryTitleRow}>
          <MaterialIcons name={icon} size={20} color={colors.primary} />
          <ThemedText style={[styles.summaryTitle, { color: colors.text }]}>{title}</ThemedText>
        </View>
        <Pressable
          onPress={() => router.push(route as any)}
          style={({ pressed }) => [styles.editButton, { opacity: pressed ? 0.7 : 1 }]}
        >
          <MaterialIcons name="edit" size={16} color={colors.primary} />
          <ThemedText style={[styles.editText, { color: colors.primary }]}>Edit</ThemedText>
        </Pressable>
      </View>
      <View style={styles.summaryContent}>{children}</View>
    </View>
  );

  const InfoRow = ({ label, value }: { label: string; value: string | null | undefined }) => (
    <View style={styles.infoRow}>
      <ThemedText style={[styles.infoLabel, { color: colors.textSecondary }]}>{label}</ThemedText>
      <ThemedText style={[styles.infoValue, { color: colors.text }]}>{value || "-"}</ThemedText>
    </View>
  );

  if (submitted) {
    return (
      <View style={[styles.successContainer, { backgroundColor: colors.background }]}>
        <View style={[styles.successIcon, { backgroundColor: colors.emerald?.background || "#D1FAE5" }]}>
          <MaterialIcons name="check-circle" size={64} color={colors.emerald?.text || "#059669"} />
        </View>
        <ThemedText style={[styles.successTitle, { color: colors.text }]}>Application Submitted!</ThemedText>
        <ThemedText style={[styles.successText, { color: colors.textSecondary }]}>
          Thank you for applying to RockForce! We will review your application and contact you soon.
        </ThemedText>
        <Button
          text="Return Home"
          icon="home"
          iconSize={18}
          iconColor="white"
          onPress={() => router.replace("/")}
          style={[styles.homeButton, { backgroundColor: colors.primary }]}
          textStyle={styles.homeButtonText}
        />
      </View>
    );
  }

  const fullName = [formData.firstName, formData.middleInitial, formData.lastName].filter(Boolean).join(" ");
  const fullAddress = [
    formData.streetLine1,
    formData.streetLine2,
    `${formData.city}, ${formData.state} ${formData.zip}`,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <ScrollView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={styles.content}>
        <View style={styles.header}>
          <MaterialIcons name="fact-check" size={40} color={colors.primary} />
          <ThemedText style={[styles.title, { color: colors.text }]}>Review Your Application</ThemedText>
          <ThemedText style={[styles.subtitle, { color: colors.textSecondary }]}>
            Please review your information before submitting
          </ThemedText>
        </View>

        {/* Personal Info */}
        <SummaryCard title="Personal Information" icon="person" route="/apply/info">
          <InfoRow label="Name" value={fullName} />
          {formData.preferredName && <InfoRow label="Preferred Name" value={formData.preferredName} />}
          <InfoRow label="Email" value={formData.email} />
          <InfoRow label="Phone" value={formData.phone} />
          <InfoRow label="Address" value={fullAddress} />
          <InfoRow label="Office" value={formData.office || "Not specified"} />
          <InfoRow
            label="Referral"
            value={
              formData.referralSource === "Referral" || formData.referralSource === "Other"
                ? `${formData.referralSource}: ${formData.referralName}`
                : formData.referralSource
            }
          />
          <InfoRow
            label="Previous RockForce Employee"
            value={formData.previouslyWorked === null ? "-" : formData.previouslyWorked ? "Yes" : "No"}
          />
        </SummaryCard>

        {/* Physical Requirements */}
        <SummaryCard title="Physical Requirements" icon="fitness-center" route="/apply/physical">
          <View style={[styles.badge, { backgroundColor: colors.emerald?.background || "#D1FAE5" }]}>
            <MaterialIcons name="check-circle" size={16} color={colors.emerald?.text || "#059669"} />
            <ThemedText style={[styles.badgeText, { color: colors.emerald?.text || "#059669" }]}>
              All requirements confirmed
            </ThemedText>
          </View>
        </SummaryCard>

        {/* Additional Questions */}
        <SummaryCard title="Additional Questions" icon="help" route="/apply/additional">
          <InfoRow
            label="Heat-related injury history"
            value={formData.heatInjury === null ? "-" : formData.heatInjury ? "Yes" : "No"}
          />
          <InfoRow
            label="Has transportation"
            value={formData.hasTransportation === null ? "-" : formData.hasTransportation ? "Yes" : "No"}
          />
          <InfoRow
            label="Event services experience"
            value={formData.eventServicesExperience === null ? "-" : formData.eventServicesExperience ? "Yes" : "No"}
          />
          <InfoRow
            label="Has smartphone"
            value={formData.hasCellPhone === null ? "-" : formData.hasCellPhone ? "Yes" : "No"}
          />
          {formData.hasAdditionalInfo && formData.additionalInfo && (
            <InfoRow label="Additional info" value={formData.additionalInfo} />
          )}
        </SummaryCard>

        {/* Confirmation */}
        <View style={[styles.confirmationCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <CheckboxRow
            label="I confirm that all the information I have provided is accurate and complete to the best of my knowledge."
            checked={confirmed}
            onToggle={() => setConfirmed(!confirmed)}
          />
        </View>

        {error && (
          <View style={[styles.errorCard, { backgroundColor: colors.attention?.background || "#FEE2E2" }]}>
            <MaterialIcons name="error" size={20} color={colors.attention?.text || "#DC2626"} />
            <ThemedText style={[styles.errorText, { color: colors.attention?.text || "#DC2626" }]}>{error}</ThemedText>
          </View>
        )}

        <View style={styles.buttonRow}>
          <Button
            text="Back"
            icon="arrow-left"
            iconPosition="left"
            iconSize={18}
            iconColor={colors.text}
            onPress={() => router.push("/apply/additional")}
            style={[styles.backButton, { borderColor: colors.border }]}
            textStyle={[styles.backButtonText, { color: colors.text }]}
          />
          <Button
            text={submitting ? "Submitting..." : "Submit Application"}
            icon={submitting ? undefined : "send"}
            iconPosition="right"
            iconSize={18}
            iconColor="white"
            onPress={handleSubmit}
            enabled={confirmed && !submitting}
            hoverColor={colors.complete.background}
            style={[
              styles.submitButton,
              {
                backgroundColor: confirmed && !submitting ? colors.complete.border || colors.primary : colors.border,
                opacity: confirmed && !submitting ? 1 : 0.5,
              },
            ]}
            textStyle={styles.submitButtonText}
          />
        </View>

        <Button
          text="Start Over"
          onPress={handleStartOver}
          style={[styles.startOverButton, { borderColor: colors.border }]}
          textStyle={[styles.startOverText, { color: colors.textSecondary }]}
        />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 20,
    gap: 16,
  },
  header: {
    alignItems: "center",
    gap: 12,
    marginBottom: 8,
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    textAlign: "center",
  },
  subtitle: {
    fontSize: 16,
    textAlign: "center",
    lineHeight: 24,
  },
  summaryCard: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
  },
  summaryHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  summaryTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  summaryTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  editButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    padding: 4,
  },
  editText: {
    fontSize: 14,
  },
  summaryContent: {
    gap: 8,
  },
  infoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
  },
  infoLabel: {
    fontSize: 14,
    flex: 1,
  },
  infoValue: {
    fontSize: 14,
    fontWeight: "500",
    flex: 2,
    textAlign: "right",
  },
  badge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
    alignSelf: "flex-start",
  },
  badgeText: {
    fontSize: 13,
    fontWeight: "500",
  },
  confirmationCard: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 12,
  },
  errorCard: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 12,
    borderRadius: 8,
  },
  errorText: {
    flex: 1,
    fontSize: 14,
  },
  buttonRow: {
    flexDirection: "row",
    gap: 12,
  },
  backButton: {
    flex: 1,
    borderRadius: 8,
    minHeight: 52,
    borderWidth: 1,
    backgroundColor: "transparent",
  },
  backButtonText: {
    fontSize: 16,
    fontWeight: "600",
  },
  submitButton: {
    flex: 2,
    borderRadius: 8,
    minHeight: 52,
  },
  submitButtonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
  startOverButton: {
    borderRadius: 8,
    minHeight: 44,
    borderWidth: 1,
    backgroundColor: "transparent",
  },
  startOverText: {
    fontSize: 14,
  },
  successContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    gap: 20,
  },
  successIcon: {
    width: 120,
    height: 120,
    borderRadius: 60,
    alignItems: "center",
    justifyContent: "center",
  },
  successTitle: {
    fontSize: 28,
    fontWeight: "bold",
    textAlign: "center",
  },
  successText: {
    fontSize: 16,
    textAlign: "center",
    lineHeight: 24,
  },
  homeButton: {
    borderRadius: 8,
    minHeight: 52,
    minWidth: 200,
    marginTop: 8,
  },
  homeButtonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
});
